import type { Inscripcion, Intento, ResumenCurso } from '../domain/types';
import type {
  AcademiaRepo,
  RespuestaElegida,
  ResultadoIntento,
  RevisionRespuesta,
} from './AcademiaRepo';
import { CURSOS, ESPACIO_BIMBO, PREMIOS_BIMBO } from './contenido/catalogo';

interface MovimientoPuntos {
  cursoId: string;
  puntos: number;
  motivo: string;
  at: string;
}

interface EstadoGuardado {
  inscripciones: Record<string, Inscripcion>;
  intentos: Intento[];
  movimientosPuntos: MovimientoPuntos[];
}

const BONUS_NOTA_PERFECTA = 50;

function estadoVacio(): EstadoGuardado {
  return { inscripciones: {}, intentos: [], movimientosPuntos: [] };
}

/**
 * Implementacion de fase 0: el contenido sale de un archivo y el avance de localStorage.
 *
 * Dos decisiones que no son casuales:
 *
 * 1. La clave incluye el id del trabajador. Dos personas que usen el mismo celular —cosa
 *    normal en campo— no deben ver el avance de la otra.
 *
 * 2. Los puntos se guardan como MOVIMIENTOS y el saldo es su suma, igual que hara la tabla
 *    `academia.puntos_movimientos`. Un contador mutable no se puede explicar cuando alguien
 *    reclame de donde salio un numero, y ademas obligaria a cambiar la logica al migrar.
 */
export class LocalRepo implements AcademiaRepo {
  private readonly clave: string;

  constructor(workerId: string) {
    this.clave = `academia:${workerId}`;
  }

  private leer(): EstadoGuardado {
    try {
      const crudo = localStorage.getItem(this.clave);
      if (!crudo) return estadoVacio();
      return { ...estadoVacio(), ...(JSON.parse(crudo) as Partial<EstadoGuardado>) };
    } catch {
      // JSON corrupto o almacenamiento bloqueado: se arranca de cero en vez de romper la app.
      return estadoVacio();
    }
  }

  private escribir(estado: EstadoGuardado): void {
    try {
      localStorage.setItem(this.clave, JSON.stringify(estado));
    } catch {
      // Modo incognito o almacenamiento lleno: la sesion sigue, solo no persiste.
    }
  }

  persisteEnServidor(): boolean {
    return false;
  }

  async obtenerEspacio(slug: string) {
    return slug === ESPACIO_BIMBO.slug ? ESPACIO_BIMBO : null;
  }

  async listarCursos(espacioId: string) {
    // `proximamente` entra: el recurso lo VE en su ruta aunque no pueda tomarlo. Sin ellos,
    // la pantalla de modulos parece un catalogo de dos cursos y ya.
    return CURSOS.filter(
      (c) => c.espacioId === espacioId && (c.estado === 'publicado' || c.estado === 'proximamente'),
    ).sort((a, b) => a.orden - b.orden);
  }

  async listarInsignias(): Promise<string[]> {
    const estado = this.leer();
    const codigos = new Set<string>();
    for (const intento of estado.intentos) {
      if (!intento.aprobado) continue;
      const curso = CURSOS.find((c) => c.id === intento.cursoId);
      if (curso) codigos.add(curso.slug);
      codigos.add('arranque');
      if (intento.nota === 100) codigos.add('nota_perfecta');
    }
    return [...codigos];
  }

  /** Sin backend la respuesta esta en el archivo, asi que se resuelve en memoria. */
  async revisarRespuesta(opcionId: string): Promise<RevisionRespuesta> {
    for (const curso of CURSOS) {
      for (const pregunta of curso.preguntas) {
        const elegida = pregunta.opciones.find((o) => o.id === opcionId);
        if (!elegida) continue;
        return {
          correcta: Boolean(elegida.esCorrecta),
          opcionCorrectaId: pregunta.opciones.find((o) => o.esCorrecta)?.id ?? null,
        };
      }
    }
    throw new Error('No existe esa alternativa.');
  }

  async obtenerCurso(cursoId: string) {
    return CURSOS.find((c) => c.id === cursoId) ?? null;
  }

  async obtenerInscripcion(cursoId: string) {
    return this.leer().inscripciones[cursoId] ?? null;
  }

  async listarIntentos(cursoId: string) {
    return this.leer().intentos.filter((i) => i.cursoId === cursoId);
  }

  async obtenerPuntos() {
    return this.leer().movimientosPuntos.reduce((suma, m) => suma + m.puntos, 0);
  }

  async solicitarCanje(): Promise<boolean> {
    // Sin backend no hay a quien pedirle la aprobacion: se dice, no se finge.
    return false;
  }

  async listarPremios(espacioId: string) {
    return PREMIOS_BIMBO.filter((p) => p.espacioId === espacioId && p.activo).sort(
      (a, b) => a.orden - b.orden,
    );
  }

  async listarResumen(espacioId: string): Promise<ResumenCurso[]> {
    const cursos = await this.listarCursos(espacioId);
    const estado = this.leer();
    return cursos.map((curso) => {
      const inscripcion = estado.inscripciones[curso.id] ?? null;
      const intentos = estado.intentos.filter((i) => i.cursoId === curso.id);
      const mejorNota = intentos.length ? Math.max(...intentos.map((i) => i.nota)) : null;
      const totalBloques = curso.lecciones.reduce((n, l) => n + l.bloques.length, 0);
      const vistos = inscripcion?.bloquesVistos.length ?? 0;
      // Un curso bloqueado no cuenta avance: no tiene bloques que ver.
      return {
        curso,
        inscripcion,
        mejorNota,
        aprobado: intentos.some((i) => i.aprobado),
        porcentajeAvance: totalBloques === 0 ? 0 : Math.round((100 * vistos) / totalBloques),
      };
    });
  }

  async marcarBloque(cursoId: string, bloqueId: string) {
    const estado = this.leer();
    const ahora = new Date().toISOString();
    const actual: Inscripcion = estado.inscripciones[cursoId] ?? {
      cursoId,
      estado: 'en_curso',
      iniciadoAt: ahora,
      completadoAt: null,
      bloquesVistos: [],
    };

    if (!actual.bloquesVistos.includes(bloqueId)) {
      actual.bloquesVistos = [...actual.bloquesVistos, bloqueId];
    }
    estado.inscripciones[cursoId] = actual;
    this.escribir(estado);
  }

  async calificarIntento(
    cursoId: string,
    respuestas: RespuestaElegida[],
  ): Promise<ResultadoIntento> {
    const curso = await this.obtenerCurso(cursoId);
    if (!curso) throw new Error(`No existe el curso ${cursoId}`);

    const total = curso.preguntas.length;
    const correctas = curso.preguntas.filter((pregunta) => {
      const elegida = respuestas.find((r) => r.preguntaId === pregunta.id);
      if (!elegida) return false;
      return pregunta.opciones.some((o) => o.id === elegida.opcionId && o.esCorrecta);
    }).length;

    const nota = total === 0 ? 0 : Math.round((100 * correctas) / total);
    const aprobado = nota >= curso.notaMinima;

    const estado = this.leer();
    const yaAprobadoAntes = estado.intentos.some((i) => i.cursoId === cursoId && i.aprobado);

    let puntosGanados = 0;
    if (aprobado && !yaAprobadoAntes) {
      puntosGanados = nota + (nota === 100 ? BONUS_NOTA_PERFECTA : 0);
      estado.movimientosPuntos.push({
        cursoId,
        puntos: puntosGanados,
        motivo: nota === 100 ? 'aprobado_nota_perfecta' : 'aprobado',
        at: new Date().toISOString(),
      });
    }

    estado.intentos.push({
      id: `i-${estado.intentos.length + 1}`,
      cursoId,
      nota,
      correctas,
      total,
      aprobado,
      terminadoAt: new Date().toISOString(),
    });

    if (aprobado && estado.inscripciones[cursoId]) {
      estado.inscripciones[cursoId].estado = 'completado';
      estado.inscripciones[cursoId].completadoAt = new Date().toISOString();
    }

    this.escribir(estado);
    return { nota, correctas, total, aprobado, puntosGanados, yaAprobadoAntes };
  }
}
